import { FunctionComponent, useEffect } from "react";
import { Hero } from "@App/Components/Hero";
import { ContactUs } from "@App/Components/ContactUs";
import { TriangleDivider } from "@App/Components/TriangleDivider";
import { Stepper } from "@App/Components/Stepper/Stepper";
import { useServices } from "@App/hooks/api/useServices";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useHome } from "@App/hooks/useHome";
import { ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored } from "@App/utils/conditionals/combinations";
import { Loader } from "@App/Components/Loader";
import { ServiceStep } from "@App/Pages/Partner/Partials/ServiceStep";
import { Benefits } from "@App/Pages/Partner/Partials/Benefits";
import { TireServiceStep } from "@App/Pages/Partner/Partials/TireServiceStep";
import { SecondaryTireServiceStep } from "@App/Pages/Partner/Partials/SecondaryTireServiceStep";
import { LocationStepDefinition } from "@App/Components/molecules/whitelabel/home/stepDefinitions/LocationStepDefinition";
import { useLocationStepDefinition } from "@App/hooks/whitelabel/home/stepper/useLocationStepDefinition";
import { Step } from "@App/Components/Stepper/Step";
import { FontSize } from "@App/utils/tailwind/fontsize";
import { EngineTypeStep } from "@App/Pages/Partner/Partials/EngineTypeStep";
import { BrandFilter } from "@App/Components/Filters";
import { ServiceIsInspection } from "@App/utils/conditionals/services";

export type HomeProps = {
  onSubmit: () => void;
};

export const Home: FunctionComponent<HomeProps> = ({ onSubmit }: HomeProps) => {
  const location = useLocation();
  const { t } = useTranslation();

  // step definitions
  const locationStepProps = useLocationStepDefinition(onSubmit);

  const { loading, error, services } = useServices();

  const {
    currentStep,
    setCurrentStep,
    organization,
    service,
    availableSubservicesLoading,
    selectService,
    selectTireServiceTypeAndService,
    secondaryTireStepSelection,
    tireStepSelection,
    showSecondaryTireStep,
    selectEngine,
    selectBrand,
    bevSupport,
    brand,
  } = useHome();

  useEffect(() => {
    if (Number.parseInt(window.location.hash.substring(1)) > 0) {
      setCurrentStep(Number.parseInt(window.location.hash.substring(1)));
    }
  }, [location]);

  useEffect(() => {
    if (!service) {
      setCurrentStep(0);
    } else if (currentStep === 0) {
      setCurrentStep(1);
    }
  }, [service]);

  if (error) {
    console.log(error);
  }

  return (
    <div data-testid={"home-page"}>
      <Hero
        organization={organization}
        title={t("hero.title", { organization: organization?.name })}
        subtitle={t("hero.subtitle", { organization: organization?.name })}
        backgroundImage={
          organization?.settings?.hero.background_image_url ??
          "/images/brand/arval-banner-darkened.jpg"
        }
        showPoweredBy={false}
        titleFontSize={FontSize["md:text-5xl"]}
      />
      <div className="bg-white px-5 pt-5 container">
        <Stepper currentStep={currentStep} onChange={setCurrentStep}>
          <Step
            title={t("Waehlen Sie einen Service.")}
            subtitle={service?.pivot.subtitle ? service.pivot.subtitle : null}
            completedTitle={"Service: "}
            completedGoBackLinkTitle={
              service
                ? service.pivot.display_name
                  ? service.pivot.display_name
                  : service.name
                : ""
            }
          >
            <ServiceStep
              service={service}
              services={services.filter((value, index, self) => self.indexOf(value) === index)}
              loading={loading}
              error={error}
              selectService={selectService}
              organization={organization}
            />
          </Step>
          {availableSubservicesLoading && <Loader />}
          {ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Welchen Service benötigen Sie?"}
                subtitle={null}
                completedTitle={"Reifenservice" + ": "}
                completedGoBackLinkTitle={tireStepSelection}
              >
                <TireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={false}
                />
              </Step>
            )}
          {showSecondaryTireStep &&
            ServiceIsWheelChangeOrServiceIsAutoAboOrServiceIsWheelChangeTiresstored(service) &&
            !availableSubservicesLoading && (
              <Step
                title={"Sind die Reifen eingelagert?"}
                subtitle={null}
                completedTitle={"Sind die Reifen eingelagert?" + ": "}
                completedGoBackLinkTitle={secondaryTireStepSelection}
              >
                <SecondaryTireServiceStep
                  services={services}
                  selectService={selectTireServiceTypeAndService}
                  organization={organization}
                  secondary={true}
                />
              </Step>
            )}
          {service && ServiceIsInspection(service) && !availableSubservicesLoading && (
            <Step
              title={t("Antriebsart")}
              subtitle={null}
              completedTitle={t("Antriebsart") + ": "}
              completedGoBackLinkTitle={
                bevSupport ? t("Elektro-/Hybridmotor") : t("Verbrennungsmotor")
              }
            >
              <EngineTypeStep selectEngine={selectEngine} />
            </Step>
          )}
          {service && ServiceIsInspection(service) && !availableSubservicesLoading && (
            <Step
              title={t("Waehlen Sie Ihre Marke.")}
              completedTitle={"Marke: "}
              completedGoBackLinkTitle={brand ? brand.name : ""}
            >
              <BrandFilter onSubmit={selectBrand} />
            </Step>
          )}
          <LocationStepDefinition {...locationStepProps} />
        </Stepper>
      </div>
      <div className="bg-neutral-100">
        <TriangleDivider direction="left" background={"bg-white"} />
        <div className="px-5 py-5 container md:pb-20">
          <div className="md:grid md:grid-cols-[50%_50%]">
            {organization && <Benefits organization={organization} />}
            <div className="pt-5 md:pt-0 md:ml-12">
              <ContactUs />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
